import type { MenuProps } from 'antd'
import type { MenuTreeNode } from '@/api/menu'

type DropdownItem = Exclude<NonNullable<MenuProps['items']>[number], null | undefined>

export const getMenuTitle = (menu: MenuTreeNode, isEnUS: boolean): string => {
  if (isEnUS && menu.title_en) {
    return menu.title_en
  }
  return menu.title || menu.menu_name
}

export const normalizeMenuPath = (path: string): string => {
  if (!path) {
    return '/'
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return normalizedPath.replace(/\/+/g, '/').replace(/\/+$/, '') || '/'
}

const buildMenuPath = (parentPath: string, currentPath: string): string => {
  const normalizedCurrentPath = currentPath === '/' ? '' : currentPath
  return normalizeMenuPath(`${parentPath}/${normalizedCurrentPath}`)
}

const isPathMatch = (pathname: string, candidatePath: string): boolean => {
  return pathname === candidatePath || pathname.startsWith(`${candidatePath}/`)
}

export const renderDropdownItems = (
  menu: MenuTreeNode,
  parentPath: string,
  navigate: (path: string) => void,
  isEnUS: boolean
): MenuProps['items'] => {
  const visibleChildren = menu.child
    ?.filter(child => child.is_show !== 0)
    .sort((a, b) => a.sort - b.sort) || []

  if (visibleChildren.length === 0) {
    return []
  }

  return visibleChildren.map(child => {
    const fullChildPath = buildMenuPath(parentPath, child.url)

    const visibleGrandchildren = child.child
      ?.filter(gc => gc.is_show !== 0)
      .sort((a, b) => a.sort - b.sort) || []

    if (visibleGrandchildren.length > 0) {
      return {
        type: 'group' as const,
        label: getMenuTitle(child, isEnUS),
        children: visibleGrandchildren.map(gc => {
          const fullPath = buildMenuPath(fullChildPath, gc.url)

          if (gc.child && gc.child.length > 0) {
            const visibleFourthLevelChildren = gc.child
              .filter(c => c.is_show !== 0)
              .sort((a, b) => a.sort - b.sort)
            const firstVisibleFourthLevelChild = visibleFourthLevelChildren[0]

            if (firstVisibleFourthLevelChild) {
              const fullFourthLevelPath = buildMenuPath(fullPath, firstVisibleFourthLevelChild.url)
              return {
                key: fullFourthLevelPath,
                label: getMenuTitle(gc, isEnUS),
                onClick: () => navigate(fullFourthLevelPath),
              }
            }
          }

          return {
            key: fullPath,
            label: getMenuTitle(gc, isEnUS),
            onClick: () => navigate(fullPath),
          }
        }),
      }
    }

    if (child.child && child.child.length > 0) {
      const visibleNestedChildren = child.child
        .filter(c => c.is_show !== 0)
        .sort((a, b) => a.sort - b.sort)
      const firstVisibleNestedChild = visibleNestedChildren[0]

      if (firstVisibleNestedChild) {
        const fullPath = buildMenuPath(fullChildPath, firstVisibleNestedChild.url)
        return {
          key: fullPath,
          label: getMenuTitle(child, isEnUS),
          onClick: () => navigate(fullPath),
        }
      }
    }

    return {
      key: fullChildPath,
      label: getMenuTitle(child, isEnUS),
      onClick: () => navigate(fullChildPath),
    }
  })
}

const visitDropdownItems = (
  items: MenuProps['items'],
  visitor: (item: DropdownItem) => void
): void => {
  items?.forEach(item => {
    if (!item) {
      return
    }

    visitor(item)

    if ('children' in item && Array.isArray(item.children)) {
      visitDropdownItems(item.children, visitor)
    }
  })
}

export const getActiveDropdownKeys = (
  items: MenuProps['items'],
  pathname: string
): string[] => {
  const normalizedPathname = normalizeMenuPath(pathname)
  let bestMatchKey: string | null = null
  let bestMatchLength = -1

  visitDropdownItems(items, (item) => {
    if (!('key' in item) || item.key == null) {
      return
    }

    const itemKey = String(item.key)
    const normalizedItemKey = normalizeMenuPath(itemKey)

    if (!isPathMatch(normalizedPathname, normalizedItemKey)) {
      return
    }

    if (normalizedItemKey.length > bestMatchLength) {
      bestMatchKey = itemKey
      bestMatchLength = normalizedItemKey.length
    }
  })

  return bestMatchKey ? [bestMatchKey] : []
}

export const isMenuActive = (pathname: string, menuUrl: string): boolean => {
  const normalizedMenuUrl = normalizeMenuPath(menuUrl)
  const normalizedPathname = normalizeMenuPath(pathname)

  if (normalizedMenuUrl === '/') {
    return normalizedPathname === '/'
  }

  return isPathMatch(normalizedPathname, normalizedMenuUrl)
}
